import { useResizeObserver } from "$root/hooks/useResizeObserver";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import React, { type ReactNode, type RefObject, createContext, useState } from "react";
import {
	type BreakpointSize,
	type BreakpointTiers,
	atLeast,
	atMost,
	computeSize,
	pickStage,
	toSizeTiers,
} from "./breakpoints";

/**
 * One size-ordered variant for {@link Variant}. List them widest→narrowest: each renders `from` its
 * named size upward (Tailwind-style "this size and wider"), and the final stage (which omits `from`) is
 * the narrowest fallback. Typed against the declared-size union `Name`, so a `from` typo is a compile error.
 */
export type Stage<Name extends BreakpointSize = BreakpointSize> = {
	/** Render this stage from `name` upward (the container is this size or wider). Omit on the last stage. */
	from?: Name;
	render: ReactNode;
};

/** The component + hook API for one breakpoint scope, bound to its declared-size union `Name`. */
export type ContainerBreakpointsApi<Name extends BreakpointSize> = {
	/**
	 * Scopes the breakpoints to a measured container. Renders nothing of its own (no layout) — it only
	 * watches `track`'s width and provides the current size to descendants. Put your layout element
	 * inside, or around, as you like.
	 */
	Breakpoints: (props: { track: RefObject<HTMLElement | null>; children: ReactNode }) => React.JSX.Element;
	/** Renders exactly one of `stages` for the current width — see {@link Stage}. */
	Variant: (props: { stages: readonly Stage<Name>[] }) => React.JSX.Element;
	/** The container's current breakpoint size. */
	useSize: () => Name;
	/** `true` when the container is `name` or wider. */
	useAtLeast: (name: Name) => boolean;
	/** `true` when the container is `name` or narrower. */
	useAtMost: (name: Name) => boolean;
};

/**
 * Create a container-width breakpoint scope bound to a fixed breakpoint size scale. This is a pure layout
 * primitive — element/container queries you can branch render logic on — with **no** business or toolbar
 * opinion. It measures one element's width and lets descendants render different subtrees as it narrows.
 *
 * Declare a min container width per size (Tailwind-style, `mobile-first`: each size means "this width and
 * up"). List them with descending widths; give the smallest `0` so it acts as the floor. The declared
 * sizes become a string-literal union `Name`, threaded through every consumer touchpoint (`<Variant>`
 * `from`, `useSize`, `useAtLeast`/`useAtMost`), so a mistyped size is a compile error.
 *
 * Call it once at module scope and destructure the pieces (hooks into `use*`-named consts):
 * ```tsx
 * const { Breakpoints, Variant, useSize } = createContainerBreakpoints({
 *   lg: 880,  // >= 880  → lg
 *   sm: 400,  // >= 400  → sm
 *   xs: 0,    // <  400  → xs (floor)
 * });
 *
 * function Header({ boxRef }: { boxRef: RefObject<HTMLDivElement | null> }) {
 *   return (
 *     <Breakpoints track={boxRef}>
 *       <Variant stages={[
 *         { from: "lg", render: <FullControls /> },  // shown from 880px up
 *         { render: <CompactControls /> },           // narrowest fallback
 *       ]} />
 *     </Breakpoints>
 *   );
 * }
 * // …or branch imperatively where `<Variant>` doesn't fit:
 * const iconMode = useAtMost("xs");
 * ```
 *
 * The pieces are bound together because they share one React context — the engine that decides the
 * current size; `<Breakpoints>` is the provider and must wrap any `<Variant>` / `use*` usage.
 */
export function createContainerBreakpoints<const T extends BreakpointTiers>(
	tiers: T,
): ContainerBreakpointsApi<Extract<keyof T, BreakpointSize>> {
	type Name = Extract<keyof T, BreakpointSize>;

	// Normalized + validated once at creation (widest → narrowest); a misordered config throws here.
	const sizeTiers = toSizeTiers(tiers);

	const SizeContext = createContext<BreakpointSize | null>(null);
	const useSize = (): Name => useMandatoryContext(SizeContext) as Name;
	const useAtLeast = (name: Name): boolean => atLeast(useSize(), name);
	const useAtMost = (name: Name): boolean => atMost(useSize(), name);

	function Breakpoints({
		track,
		children,
	}: {
		track: RefObject<HTMLElement | null>;
		children: ReactNode;
	}): React.JSX.Element {
		// Measure the tracked element and swap the current size only when it changes, so a per-pixel resize
		// does not re-render descendants — only crossing a tier boundary does.
		const [size, setSize] = useState<BreakpointSize>(() => computeSize(undefined, sizeTiers));
		useResizeObserver({
			ref: track as RefObject<HTMLElement>,
			box: "border-box",
			onResize: ({ width }) => {
				const next = computeSize(width, sizeTiers);
				setSize((prev) => (prev === next ? prev : next));
			},
		});

		return <SizeContext.Provider value={size}>{children}</SizeContext.Provider>;
	}

	function Variant({ stages }: { stages: readonly Stage<Name>[] }): React.JSX.Element {
		const size = useSize();
		return <>{pickStage(stages, size)?.render ?? null}</>;
	}

	return { Breakpoints, Variant, useSize, useAtLeast, useAtMost };
}
